/**
 * A real map picture of a place, from our own tile server
 * (tiles.trip-planner.com.au, the same style as the planner's maps). Used by
 * the ca_preview store skin wherever a listing has no photo of its own: the
 * store never shows an empty image well, and a map of where the place is
 * is honest imagery (a stock photo would pretend to be the place).
 */
import type { Entity } from '../types';

export const PLACE_TILES = 'https://tiles.trip-planner.com.au';
/**
 * The store's map style (infrastructure/tileserver/styles/store): Warm Paper
 * land, quiet slate water, no sea names or foreign borders. ca_preview only;
 * shared-trip pictures keep `basic`.
 */
export const PLACE_STYLE = 'store';

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

/** First usable lat/lng on an entity (GPS, then waypoint), or null. */
export function entityLatLng(entity: Pick<Entity, 'addresses'> | null | undefined): { lat: number; lng: number } | null {
  for (const a of entity?.addresses ?? []) {
    const lat = num(a?.gps_latitude) ?? num(a?.waypoint_latitude);
    const lng = num(a?.gps_longitude) ?? num(a?.waypoint_longitude);
    if (lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0)) {
      return { lat, lng };
    }
  }
  return null;
}

/**
 * Static map URL. `zoom` is on the same scale as Google Maps (a 256 CSS px
 * world at zoom 0). Width/height are CSS pixels; the picture comes back at
 * 2x, as WebP.
 *
 * The tile server renders at most 2048 device px a side, i.e. 1024 CSS px at
 * @2x: anything bigger is a 400. A larger box is scaled down to fit (aspect
 * kept) and the zoom lowered to match, so the picture still covers the same
 * ground; the browser stretches it the rest of the way.
 */
export const PLACE_MAP_MAX = 1024;
export function placeMapUrl(lat: number, lng: number, width: number, height: number, zoom = 10): string {
  let w = Math.max(32, Math.round(width));
  let h = Math.max(32, Math.round(height));
  let z = zoom;
  const big = Math.max(w, h);
  if (big > PLACE_MAP_MAX) {
    const s = PLACE_MAP_MAX / big;
    w = Math.max(32, Math.min(PLACE_MAP_MAX, Math.round(w * s)));
    h = Math.max(32, Math.min(PLACE_MAP_MAX, Math.round(h * s)));
    z = Math.max(0, Math.round((zoom + Math.log2(s)) * 100) / 100);
  }
  return `${PLACE_TILES}/styles/${PLACE_STYLE}/static/${lng.toFixed(4)},${lat.toFixed(4)},${z}/${w}x${h}@2x.webp`;
}

/**
 * Australia, framed to fill a map box: pictures from the same tile server,
 * saved under public/images so the explorer's first paint needs no render.
 */
export const AU_MAP_WIDE = '/images/ca-map-au-wide.webp'; // 1000x760, zoom 4.35
export const AU_MAP_TALL = '/images/ca-map-au-tall.webp'; // 400x760, zoom 3.45

/**
 * True for a point well off the mainland and Tasmania (Christmas and Cocos
 * Islands, Lord Howe, Norfolk, the Coral Sea): places the country-scale
 * basemap doesn't draw, so a pin there reads as floating in the ocean.
 * [lng, lat], as GeoJSON has it. Exported for tests.
 */
export function isFarOffshore([lng, lat]: [number, number]): boolean {
  return lng < 112.5 || lng > 154 || lat > -10.4 || lat < -44;
}
